import { useState } from "react";
import { Send } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSettings, buildWhatsAppLink, onWhatsAppClick } from "@/contexts/SettingsContext";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { toast } from "sonner";

export default function ContactPage() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, type: "consultation" };
      if (!payload.email) delete payload.email;
      if (!payload.phone) delete payload.phone;
      await api.post("/inquiries", payload);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success("Thanks! We'll be in touch within the business day.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full px-4 py-3 border border-[color:var(--bda-border)] focus:outline-none focus:border-[color:var(--bda-blue-glow)] text-sm bg-[color:var(--bda-surface)] text-[color:var(--bda-text)] placeholder:text-[color:var(--bda-text-dim)]";

  return (
    <div className="mx-auto max-w-[935px] px-5 md:px-10 py-10 md:py-16" data-testid="contact-page">
      <div className="grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-[color:var(--bda-text-dim)] mb-3">Start a project</div>
          <h1 className="font-heading text-3xl md:text-5xl font-light tracking-tight text-[color:var(--bda-text)] leading-tight">
            Build with us.
          </h1>
          <p className="mt-5 text-[color:var(--bda-text-muted)] max-w-md leading-relaxed">
            Tell us about your project. Our team will respond within the business day.
          </p>
          <a
            href={buildWhatsAppLink(settings?.whatsapp_number)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onWhatsAppClick}
            data-testid="contact-whatsapp-btn"
            className="inline-flex items-center gap-2 mt-7 bg-[#25D366] hover:bg-[#1FB855] text-white px-6 py-4 text-sm font-medium tracking-wide transition-colors"
          >
            <WhatsAppIcon className="w-4 h-4" color="#FFFFFF" /> Or message us on WhatsApp
          </a>
          <div className="mt-10 space-y-1.5 text-sm text-[color:var(--bda-text-muted)]">
            {settings?.contact_email && <div>Email: <span className="text-[color:var(--bda-text)]">{settings.contact_email}</span></div>}
            {settings?.whatsapp_number && <div>WhatsApp: <span className="text-[color:var(--bda-text)]">{settings.whatsapp_number}</span></div>}
          </div>
        </div>

        <form onSubmit={submit} className="border border-[color:var(--bda-border)] p-7 space-y-3 bg-[color:var(--bda-surface)]" data-testid="contact-form">
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="contact-name-input" className={inputCls} />
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="contact-email-input" className={inputCls} />
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" data-testid="contact-phone-input" className={inputCls} />
          <textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your project…" data-testid="contact-message-input" className={inputCls} />
          <button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 bg-[color:var(--bda-blue-glow)] hover:opacity-90 text-white px-6 py-4 text-sm font-medium tracking-wide disabled:opacity-50 transition-colors"
          >
            <Send className="w-4 h-4" /> {submitting ? "Sending…" : "Request Consultation"}
          </button>
        </form>
      </div>
    </div>
  );
}
